/*
 * ETOLIE — kontroler strumienia listingu (home, kolekcja, marka).
 *
 * 04.09.2026 (ETAP 5, pozycje 5.1, 5.2 i 5.6): plik nazywał się
 * assets/etolie-home-catalog.js i obsługiwał wyłącznie home. Kolekcja miała
 * DRUGI, uboższy loader w inline <script> sekcji siatki i doczytywała PEŁNE
 * dokumenty — mediana 497 600 B na stronę, 8 883 308 B na przewinięcie katalogu
 * (pomiar 04.09). Teraz oba listingi używają tego samego kontrolera i tej samej
 * lekkiej trasy (~126 000 B na stronę).
 *
 * Dwa tryby, jeden kod:
 *   data-etolie-mode="catalog" — home: panel filtrów steruje siatką przez AJAX,
 *   data-etolie-mode="stream"  — kolekcja i marka: filtrowanie to zwykła nawigacja,
 *                                kontroler tylko doczytuje kolejne strony.
 */
(() => {
  if (customElements.get('etolie-listing-stream')) return;

  const CATEGORY_FIELD = '_etolie_category';
  const HOME_CATEGORY_PARAM = 'category';
  const FILTER_PREFIX = 'filter.';
  const PRICE_FIELDS = ['filter.v.price.gte', 'filter.v.price.lte'];
  const CACHE_LIMIT = 4;
  const PENDING_CLASS = 'etolie-home-catalog--pending';
  // Domyślny rozmiar strony listingu. Trasa lekka paginuje DOKŁADNIE tak jak
  // siatka serwerowa (marki 60, reszta 24), więc strona N to zawsze ten sam
  // wycinek katalogu — bez tego adres ?page=N wskazywałby inne produkty.
  const DEFAULT_PAGE_SIZE = 24;
  // Kotwicą pozycji jest kafelek najbliższy górze okna; 120 px omija nagłówek.
  const ANCHOR_OFFSET = 120;
  const SCROLL_MEMORY_PREFIX = 'etolie:stream:';

  // Odmiana liczebnika ma JEDNO źródło — assets/etolie-listing.js, ładowany
  // w <head> na każdym listingu. Kopia lokalna jest tylko awaryjna (gdyby ten
  // plik wystartował bez panelu filtrów).
  const productNoun = (count) => {
    if (typeof window.etolieProductNoun === 'function') return window.etolieProductNoun(count);
    if (!document.documentElement.lang.startsWith('pl')) return window.etolieT(count === 1 ? 'produkt' : 'produkty');
  if (count === 1) return window.etolieT("produkt");
    const mod10 = count % 10;
    const mod100 = count % 100;
    return mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14) ? window.etolieT("produkty") : window.etolieT("produktów");
  };

  const formatProducts = (count) => {
    if (!Number.isFinite(count)) return window.etolieT("produkty");
    return `${count} ${productNoun(count)}`;
  };

  const formatCatalogCount = (count, minimum = false) => (
    minimum ? `${window.etolieT('Co najmniej')} ${formatProducts(count)}` : formatProducts(count)
  );

  // 29.09.2026: DE „15 Produkte anzeigen” — kolejność słów z etolieShowCount (etolie-listing.js).
  const formatSubmitCount = (count, minimum = false) => {
    const prefix = minimum ? window.etolieT('co najmniej') + ' ' : '';
    if (Number.isFinite(count) && typeof window.etolieShowCount === 'function') return window.etolieShowCount(count, prefix);
    return `${window.etolieT('Pokaż')} ${prefix}${formatProducts(count)}`;
  };

  const cloneState = (state) => ({
    category: state.category,
    source: state.source,
    parameters: new URLSearchParams(state.parameters),
  });

  const stableParameters = (parameters) => {
    const entries = Array.from(parameters.entries()).sort(([leftName, leftValue], [rightName, rightValue]) => {
      const byName = leftName.localeCompare(rightName);
      return byName || leftValue.localeCompare(rightValue);
    });
    return new URLSearchParams(entries);
  };

  class EtolieListingStream extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      // 04.09.2026 (ETAP 5.1): tryb decyduje, ile kontroler bierze na siebie.
      this.mode = this.dataset.etolieMode === 'stream' ? 'stream' : 'catalog';
      this.viewName = this.dataset.etolieView || 'etolie-home';
      this.grid = this.querySelector('[data-etolie-collection-grid]');
      this.sentinel = this.querySelector('[data-home-sentinel], [data-etolie-collection-sentinel]');
      if (!this.grid) return;

      this.responseCache = new Map();
      this.primarySequence = 0;
      this.progressiveSequence = 0;
      this.loadedPageURLs = new Set();
      this.nextURL = '';
      this.retryState = null;
      this.events = new AbortController();
      const options = { signal: this.events.signal };

      // 04.09.2026 (ETAP 5.2): przeglądarka przestaje przywracać pozycję, pod
      // którą po powrocie nie ma już treści — robi to kontroler, po kotwicy
      // (id produktu) zapisanej w sessionStorage. Ustawiamy to tylko tutaj,
      // czyli wyłącznie na stronach ze strumieniem.
      try {
        if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
      } catch (error) { /* polityka przeglądarki */ }
      window.addEventListener('scroll', this.onStreamScroll.bind(this), { passive: true, signal: this.events.signal });
      window.addEventListener('pagehide', () => this.rememberScroll(false), options);
      // Na telefonie `pagehide` bywa pominięty; `visibilitychange` jest pewniejszy.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.rememberScroll(false);
      }, options);
      // Klik w kafelek to najczęstsze wyjście z listingu — zapisujemy pozycję
      // zanim przeglądarka zacznie nawigację.
      this.addEventListener('click', (event) => {
        if (event.target instanceof Element && event.target.closest('.etolie-card a[href], [data-film-card] a[href]')) this.rememberScroll();
      }, options);

      if (this.mode === 'stream') {
        this.connectStream();
        return;
      }

      this.form = this.querySelector('form.etolie-collection__controls[data-etolie-external-controls="home"]');
      this.loadMoreButton = this.querySelector('[data-home-load-more]');
      // 20.10.2026: numerowane strony SSR na home; widoczne tylko, gdy siatka to strona z serwera.
      this.homePagination = this.querySelector('[data-home-pagination]');
      this.homeGridIsSSR = false;
      this.resultCount = this.querySelector('[data-home-result-count]');
      this.status = this.querySelector('[data-home-status]');
      this.filterDetails = this.form?.querySelector('[data-etolie-filters]');
      this.submitButton = this.form?.querySelector('[data-etolie-filter-submit]');
      this.submitLabel = this.form?.querySelector('[data-etolie-filter-submit-label]');
      this.resetButton = this.form?.querySelector('[data-etolie-filter-reset]');
      this.chipsHost = this.querySelector('[data-etolie-active-chips]');
      this.defaultSort = this.dataset.defaultSort || 'manual';
      this.allSource = this.dataset.allSource || (window.Shopify.routes.root + 'collections/all');
      this.facetContainer = this.form?.querySelector('[data-etolie-facet-groups]');
      this.deferredFilters = this.form?.dataset.etolieDeferredFilters === 'true';
      this.filterSourceURL = this.form?.dataset.etolieFilterSourceUrl || this.allSource;
      this.brandTemplates = Array.from(this.querySelectorAll('template[data-home-brand-template]'));

      // 20.10.2026: sentinel nie jest już warunkiem (home ma numerowane strony); bez formularza nadal nic nie robimy.
      if (!this.form) return;

      this.facetBaseline = this.captureFacetBaseline();
      // Adres home bez stanu katalogu — baza dla replaceState w stanie domyślnym.
      this.homeURL = this.catalogFreeAbsoluteURL();
      // 04.09.2026 (scalenie): licznik zmian adresu. Wstecz/Dalej ma pierwszeństwo
      // przed odpowiedzią żądania, które było w locie, gdy klientka cofnęła stronę.
      this.locationSequence = 0;

      this.form.addEventListener('submit', this.onSubmit.bind(this), options);
      this.form.addEventListener('change', this.onFormChange.bind(this), options);
      this.form.addEventListener('input', this.onFormInput.bind(this), options);
      this.filterDetails?.addEventListener('toggle', this.onFilterToggle.bind(this), options);
      this.resetButton?.addEventListener('click', this.onResetClick.bind(this), options);
      this.loadMoreButton?.addEventListener('click', this.onLoadMoreClick.bind(this), options);
      this.chipsHost?.addEventListener('click', this.onChipClick.bind(this), options);
      window.addEventListener('pageshow', this.onPageShow.bind(this), options);
      window.addEventListener('popstate', this.onPopState.bind(this), options);

      this.appliedState = this.stateFromLocation();
      this.locationStateKey = this.catalogStateKey(this.appliedState);
      // Zatwierdzony wybór zostaje w adresie — adresem KOLEKCJI, który serwer umie
      // wyrenderować (ETAP 5). Adres '/' + filtr, którego szablon index nie
      // obsługuje, w ogóle nie powstaje, a stary link z takimi parametrami
      // czytamy tutaj raz i zamieniamy na adres kolekcji po pierwszej odpowiedzi.
      // Do tego czasu siatka SSR jest zasłonięta szkieletem.
      this.syncForm(this.appliedState, true);
      this.syncCategoryRow(this.appliedState);
      this.interleaveBrandStories();
      this.deferredFacetPromise = this.deferredFilters
        ? this.bootstrapDeferredFacets()
        : Promise.resolve(true);

      if (this.isDefaultStream(this.appliedState)) {
        // The home source collection already owns the canonical newest-first
        // order. Its first server render is therefore the final first page: no
        // client request is allowed to replace it after paint.
        const ssrNextURL = this.grid.dataset.homeNextUrl || '';
        this.nextURL = ssrNextURL ? this.sourceURL(this.appliedState, ssrNextURL).href : '';
        this.loadedPageURLs.add(this.sourceURL(this.appliedState).href);
        const total = Number.parseInt(this.grid.dataset.homeTotal, 10);
        this.appliedCount = Number.isFinite(total) ? total : null;
        this.appliedCountIsMinimum = false;
        this.requiresExactVariantFilter = false;
        this.visiblePage = Number.parseInt(this.grid.dataset.etolieCurrentPage || '1', 10);
        this.tagStreamPage(this.grid.children, this.visiblePage);
        this.homeGridIsSSR = true;
        this.prepareProgressiveLoading();
        this.setPending(false);
        this.restoreScrollFromMemory();
      } else {
        // A bookmarked non-default filter or sort is the only state that needs
        // an endpoint request because the index route cannot receive collection
        // filter parameters during its Liquid render. Do zakończenia tego
        // żądania siatka SSR jest zasłonięta szkieletem (klasa z sekcji), żeby
        // z zapisanego linku nie mignął pełny katalog.
        this.deferredFacetPromise.then(() => {
          if (!this._connected || this.locationSequence !== 0) return;
          this.loadFirstPage(this.appliedState, { announce: false, purpose: 'initial', historyMode: 'replace' });
        });
      }
    }

    disconnectedCallback() {
      if (!this._connected) return;
      this._connected = false;
      this.events?.abort();
      this.primaryController?.abort();
      this.progressiveController?.abort();
      this.deferredFacetController?.abort();
      this.intersectionObserver?.disconnect();
    }

    /* ==================================================================
       TRYB „stream" — kolekcja i kolekcja markowa (ETAP 5.1)
       Filtrowanie zostaje zwykłą nawigacją serwera; kontroler doczytuje
       kolejne strony lekką trasą i pilnuje licznika oraz paginacji.
       ================================================================== */

    connectStream() {
      this.availabilityEmpty = this.querySelector('[data-etolie-availability-empty]');
      this.heroCount = document.querySelector('.etolie-ql-collection-hero__count');
      this.submitLabel = this.querySelector('[data-etolie-filter-submit-label]');

      const currentPage = Number.parseInt(this.grid.dataset.etolieCurrentPage || '1', 10) || 1;
      this.visiblePage = currentPage;
      this.tagStreamPage(this.grid.children, currentPage);
      window.requestAnimationFrame(() => this.syncStreamCount());
      this.restoreScrollFromMemory();

      // 20.10.2026: bez sentinela kolekcja ma zwykłe strony; licznik bierze wtedy total z serwera.
      this.progressiveEnabled = Boolean(this.sentinel);
      if (!this.sentinel) return;

      this.perPage = Number.parseInt(this.grid.dataset.etoliePerPage || '', 10) || DEFAULT_PAGE_SIZE;
      const consumed = Number.parseInt(this.grid.dataset.etolieSsrConsumed || '', 10) || 0;
      // Serwer wydał już `consumed` produktów, a trasa lekka liczy tak samo jak
      // siatka, więc kolejna strona to floor(consumed / per_page) + 1. Dedup po
      // data-product-id zostaje jako siatka bezpieczeństwa.
      this.nextLightPage = Math.floor(consumed / this.perPage) + 1;
      this.nextURL = this.streamURL(this.nextLightPage);

      // Doczytywanie idzie tylko w przód, więc zastępuje paginację jedynie przy
      // starcie od strony 1. Wejście wprost na stronę N (np. z wyszukiwarki)
      // zachowuje pełną paginację z linkami do stron wcześniejszych.
      if (currentPage === 1) this.classList.add('is-progressive-nav');
      this.prepareProgressiveLoading();
    }

    streamURL(page) {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.set('view', this.viewName);
      url.searchParams.set('page', String(page));
      return url.href;
    }

    tagStreamPage(nodes, page) {
      Array.from(nodes).forEach((node) => {
        if (node.matches && node.matches('.etolie-card')) node.dataset.etolieStreamPage = String(page);
      });
    }

    revealPagination() {
      this.classList.remove('is-progressive-nav');
      this.intersectionObserver?.disconnect();
      this.sentinel?.remove();
      this.sentinel = null;
    }

    syncStreamCount() {
      if (!this.grid) return;
      // Jednostką tego listingu jest widoczny kafelek koloru, nie produkt Shopify.
      // Na jednej stronie pokazujemy dokładną liczbę kafelków. Dla dłuższej
      // kolekcji zachowujemy stabilny total Shopify do chwili doczytania całej
      // listy od strony 1, zamiast zmieniać liczbę przy każdym scrollu.
      const pages = Number.parseInt(this.grid.dataset.etoliePages || '1', 10);
      const currentPage = Number.parseInt(this.grid.dataset.etolieCurrentPage || '1', 10);
      const renderedCount = this.grid.querySelectorAll(':scope > .etolie-card').length;
      const serverCount = Number.parseInt(this.dataset.etolieResultsCount || '', 10);
      const hasCompleteProgressiveList = Boolean(this.progressiveEnabled) && currentPage === 1 && !this.nextURL;
      const exact = pages <= 1 || hasCompleteProgressiveList || !Number.isFinite(serverCount);
      const count = exact ? renderedCount : serverCount;
      const noun = productNoun(count);
      this.dataset.etolieResultsCount = String(count);
      if (this.heroCount) this.heroCount.textContent = `${count} ${noun}`;
      // 28.09.2026 (N5): total Shopify liczy też wyprzedane, więc bez pełnej listy przycisk nie podaje liczby.
      if (this.submitLabel) {
        this.submitLabel.textContent = exact ? formatSubmitCount(count) : window.etolieT('Pokaż wyniki');
      }
    }

    async loadNextStreamPage() {
      if (!this.nextURL || this.loadingMore || !this.sentinel) return;

      this.loadingMore = true;
      this.progressiveSequence += 1;
      const sequence = this.progressiveSequence;
      this.progressiveController?.abort();
      this.progressiveController = new AbortController();
      this.intersectionObserver?.unobserve(this.sentinel);
      this.sentinel?.setAttribute('aria-busy', 'true');

      try {
        let scannedPages = 0;
        let appendedCount = 0;

        while (this.nextURL && scannedPages < 40 && appendedCount === 0) {
          const pageURL = this.nextURL;
          if (this.loadedPageURLs.has(pageURL)) {
            this.nextURL = '';
            break;
          }
          const lightPage = this.nextLightPage;
          const snapshot = await this.requestSnapshot(new URL(pageURL), this.progressiveController.signal);
          if (sequence !== this.progressiveSequence) return;
          this.loadedPageURLs.add(pageURL);
          scannedPages += 1;

          const { fragment, count } = this.cloneCards(snapshot.gridSource, { lazy: true, deduplicate: true });
          appendedCount = count;
          if (count > 0) {
            const appendedCards = Array.from(fragment.children);
            this.grid.append(fragment);
            appendedCards.forEach((card) => window.etolieInitCards?.(card));
            this.tagStreamPage(appendedCards, lightPage);
            // 28.09.2026 (N8): serwer odkłada wyprzedane na koniec każdej porcji,
            // więc po doczytaniu przenosimy je za wszystkie dostępne (marki).
            if (this.grid.classList.contains('etolie-shop__grid--sold-out-last')) {
              this.grid.querySelectorAll(':scope > .etolie-card--sold-out').forEach((card) => this.grid.append(card));
            }
          }
          this.nextLightPage = lightPage + 1;
          this.nextURL = snapshot.nextURL ? this.streamURL(this.nextLightPage) : '';
        }

        this.syncStreamCount();
        if (this.nextURL) {
          this.intersectionObserver?.observe(this.sentinel);
        } else {
          this.intersectionObserver?.disconnect();
          this.sentinel?.remove();
          this.sentinel = null;
          if (this.availabilityEmpty && !this.grid.querySelector('.etolie-card')) {
            this.availabilityEmpty.hidden = false;
          }
        }
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.progressiveSequence) return;
        console.warn('[ETOLIE] Nie udało się doczytać kolejnej strony listingu.', error);
        // Bez doczytywania klientka musi mieć z powrotem linki paginacji.
        this.revealPagination();
      } finally {
        if (sequence === this.progressiveSequence) {
          this.loadingMore = false;
          this.progressiveController = null;
          this.sentinel?.removeAttribute('aria-busy');
        }
      }
    }

    /* ==================================================================
       Adres i pozycja (ETAP 5.2 + dodatek „chipy i adres na home")
       ================================================================== */

    catalogFreeAbsoluteURL() {
      const url = new URL(window.location.href);
      url.searchParams.delete(HOME_CATEGORY_PARAM);
      url.searchParams.delete('sort_by');
      url.searchParams.delete('view');
      url.searchParams.delete('page');
      Array.from(url.searchParams.keys()).forEach((name) => {
        if (name.startsWith(FILTER_PREFIX)) url.searchParams.delete(name);
      });
      return url;
    }

    historyBaseURL() {
      if (this.mode === 'stream') {
        const url = new URL(window.location.href);
        url.searchParams.delete('view');
        url.searchParams.delete('section_id');
        url.searchParams.delete('page');
        return url;
      }
      // Home w stanie domyślnym opisuje adres '/'; ze stanem filtra — adres
      // KOLEKCJI, który serwer umie wyrenderować za pierwszym razem. Dzięki temu
      // adres '/' + filtr, którego szablon index nie obsługuje, w ogóle nie powstaje.
      if (this.isDefaultStream(this.appliedState)) {
        return new URL(this.homeURL ? this.homeURL.href : window.location.href);
      }
      const url = this.sourceURL(this.appliedState);
      url.searchParams.delete('view');
      url.searchParams.delete('page');
      return url;
    }

    // Jedyne miejsce, które zapisuje adres listingu.
    // `replace` — ta sama pozycja historii (przewijanie zmienia numer strony);
    // `push`    — nowa pozycja (klientka ZATWIERDZIŁA inny wybór), dzięki czemu
    //             Wstecz/Dalej wraca do poprzedniego wyboru;
    // `none`    — adres już jest właściwy (obsługa samego popstate).
    syncHistoryURL(page = 1, mode = 'replace') {
      if (mode !== 'push' && mode !== 'replace') return;
      // Klucz stanu istnieje tylko na home (tryb „catalog"). Kolekcja przewijaniem
      // zmienia sam numer strony, więc nie ma tu czego porównywać przy popstate.
      if (this.appliedState) this.locationStateKey = this.catalogStateKey(this.appliedState);
      const url = this.historyBaseURL();
      if (page > 1) url.searchParams.set('page', String(page));
      else url.searchParams.delete('page');
      const target = `${url.pathname}${url.search}${url.hash}`;
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (target === current) return;
      try {
        window.history[mode === 'push' ? 'pushState' : 'replaceState'](window.history.state, '', target);
      } catch (error) { /* polityka przeglądarki */ }
    }

    scrollMemoryKey() {
      const url = new URL(window.location.href);
      const parts = [];
      Array.from(url.searchParams.entries()).forEach(([name, value]) => {
        if (name.startsWith(FILTER_PREFIX) || name === 'sort_by' || name === HOME_CATEGORY_PARAM) {
          parts.push(`${name}=${value}`);
        }
      });
      parts.sort();
      return `${SCROLL_MEMORY_PREFIX}${url.pathname}?${parts.join('&')}`;
    }

    anchorCard() {
      const cards = Array.from(this.grid.children).filter((child) => child.matches('.etolie-card'));
      if (!cards.length) return null;
      // Kafelki idą w kolejności dokumentu, więc `top` jest niemalejący — wystarczy
      // wyszukiwanie binarne (9 pomiarów zamiast 400 przy pełnym przewinięciu).
      let low = 0;
      let high = cards.length - 1;
      let found = 0;
      while (low <= high) {
        const middle = (low + high) >> 1;
        if (cards[middle].getBoundingClientRect().top <= ANCHOR_OFFSET) {
          found = middle;
          low = middle + 1;
        } else {
          high = middle - 1;
        }
      }
      const card = cards[found];
      return {
        card,
        top: card.getBoundingClientRect().top,
        page: Number.parseInt(card.dataset.etolieStreamPage || '1', 10) || 1,
      };
    }

    // Adres i pamięć pozycji zapisujemy z TEJ SAMEJ kotwicy i w tej samej chwili.
    // Rozdzielone (adres na zmianie strony, pamięć co 400 ms) rozjeżdżały się:
    // adres mówił „strona 17", a w pamięci stała kotwica ze strony 18 — po
    // powrocie serwer renderował stronę 17, kotwicy w niej nie było i pozycja
    // wracała na górę.
    //
    // `allowPageChange: false` jest dla ścieżki wyjścia ze strony (pagehide,
    // ukrycie karty): tam `replaceState` już nic nie zmienia, bo nawigacja jest
    // zatwierdzona. Kotwica z innej strony niż ta w adresie byłaby nie do
    // odtworzenia, więc zamiast jej zapisu zostawiamy poprzedni, zgodny wpis.
    rememberScroll(allowPageChange = true) {
      if (!this.grid || !this._connected) return;
      if (Date.now() < (this.restoringUntil || 0)) return;
      const anchor = this.anchorCard();
      if (!anchor) return;
      if (anchor.page !== this.visiblePage) {
        if (!allowPageChange) return;
        this.visiblePage = anchor.page;
        this.syncHistoryURL(anchor.page);
      }
      try {
        window.sessionStorage.setItem(this.scrollMemoryKey(), JSON.stringify({
          page: anchor.page,
          id: anchor.card.dataset.productId || '',
          dy: Math.round(-anchor.top),
        }));
      } catch (error) { /* tryb prywatny */ }
    }

    restoreScrollFromMemory() {
      const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
      const type = navigation?.type || '';
      // Pozycję odtwarzamy tylko przy powrocie i odświeżeniu. Wejście z linku ma
      // zaczynać się od góry, tak jak dziś.
      if (type !== 'back_forward' && type !== 'reload') return;
      let entry = null;
      try {
        entry = JSON.parse(window.sessionStorage.getItem(this.scrollMemoryKey()) || 'null');
      } catch (error) { entry = null; }
      if (!entry) return;

      const escapeId = window.CSS && typeof window.CSS.escape === 'function'
        ? window.CSS.escape
        : (value) => value.replace(/["\\]/g, '\\$&');
      // Kotwicą jest konkretny produkt. Jeśli serwer wyrenderował inną stronę
      // (np. klientka weszła w link z innym ?page=), kotwicy nie ma i pozycji
      // nie zmyślamy — zostaje góra listy, czyli dzisiejsze zachowanie.
      const target = entry.id
        ? this.grid.querySelector(`.etolie-card[data-product-id="${escapeId(entry.id)}"]`)
        : null;
      if (!target) return;

      // Przywracamy z powtórzeniami i ZAWSZE skokiem. Dwa powody:
      // 1. assets/base.css:32 ustawia html { scroll-behavior: smooth } — bez
      //    behavior: 'instant' przeglądarka animuje dojazd, a kolejna próba
      //    natychmiast tę animację przerywa i pozycja zostaje na zerze;
      // 2. kontroler startuje na `defer`, więc zdjęcia nie mają jeszcze
      //    wysokości i pierwsza próba może trafić w krótszy dokument.
      // Pozycja liczona jest od kotwicy (konkretnego kafelka), więc każda
      // kolejna próba tylko poprawia poprzednią.
      const delays = [0, 120, 260, 500, 900, 1600];
      let attempt = 0;
      const apply = () => {
        if (!this._connected) return;
        const top = Math.max(0, Math.round(target.getBoundingClientRect().top + window.scrollY + (entry.dy || 0)));
        // Blokada zapisu pamięci: przewinięcie w trakcie odtwarzania nie jest
        // wyborem klientki i nie może nadpisać zapamiętanej kotwicy.
        this.restoringUntil = Date.now() + 2200;
        window.scrollTo({ top, left: 0, behavior: 'instant' });
        attempt += 1;
        if (attempt < delays.length && Math.abs(window.scrollY - top) > 4) {
          window.setTimeout(apply, delays[attempt]);
        }
      };
      const startRestore = () => {
        attempt = 0;
        window.requestAnimationFrame(apply);
      };
      // Motyw ma włączone przejścia między dokumentami (assets/base.css:228
      // `@view-transition { navigation: auto }`, obsługa w assets/view-transitions.js).
      // Przy powrocie „wstecz" przeglądarka ustala pozycję nowego dokumentu przy
      // AKTYWACJI przejścia, więc każde wcześniejsze przewinięcie jest kasowane —
      // zmierzone: osiem prób w 1,1 s i `scrollY` wciąż 0. Dlatego odtwarzamy
      // pozycję najpierw od razu (gdy przejścia nie ma), a potem jeszcze raz po
      // jego zakończeniu.
      startRestore();
      window.addEventListener('pagereveal', (event) => {
        // Raz od razu (to jest ostatnia chwila PRZED pierwszym malowaniem nowego
        // dokumentu) i raz po zakończeniu przejścia.
        startRestore();
        const transition = event?.viewTransition;
        if (transition && typeof transition.finished?.then === 'function') {
          transition.finished.then(startRestore, startRestore);
        }
      }, { once: true });
    }

    onStreamScroll() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        this.scrollTicking = false;
        if (!this._connected || !this.grid) return;
        const now = Date.now();
        if (now - (this.lastScrollMemory || 0) < 250) return;
        this.lastScrollMemory = now;
        this.rememberScroll();
      });
    }

    /* ==================================================================
       Chipy aktywnych filtrów na home (dodatek do ETAPU 5)
       Klasy i markup są te same co w SSR kolekcji, więc nie ma nowego CSS,
       a przy zerze filtrów kontener zostaje `hidden` — stan domyślny bez zmian.
       ================================================================== */

    renderActiveChips() {
      if (!this.chipsHost || !this.form) return;
      const chips = [];

      const categoryInput = this.form.querySelector(`input[name="${CATEGORY_FIELD}"]:checked`);
      if (categoryInput && categoryInput.value) {
        chips.push({
          key: window.etolieT("Kategoria"),
          label: categoryInput.closest('label')?.querySelector('.etolie-collection__filter-name')?.textContent?.trim() || categoryInput.value,
          input: categoryInput,
        });
      }

      this.form.querySelectorAll(`input[name^="${FILTER_PREFIX}"]:checked`).forEach((input) => {
        if (input.type !== 'checkbox' && input.type !== 'radio') return;
        const group = input.closest('[data-etolie-fgroup]');
        chips.push({
          key: group?.querySelector('.etolie-collection__fgroup-label')?.textContent?.trim() || window.etolieT("Filtr"),
          label: input.dataset.label || input.value,
          input,
        });
      });

      const priceInputs = PRICE_FIELDS
        .map((name) => this.form.querySelector(`input[name="${name}"]`))
        .filter((input) => input && input.value !== '');
      if (priceInputs.length) {
        const from = this.form.querySelector(`input[name="${PRICE_FIELDS[0]}"]`);
        const to = this.form.querySelector(`input[name="${PRICE_FIELDS[1]}"]`);
        const parts = [];
        if (from && from.value !== '') parts.push(`${window.etolieT('Od')} ${from.value} ${window.etolieCurrency}`);
        if (to && to.value !== '') parts.push(`${window.etolieT('Do')} ${to.value} ${window.etolieCurrency}`);
        chips.push({ key: window.etolieT("Cena"), label: parts.join(' '), inputs: priceInputs });
      }

      this.chipsHost.replaceChildren();
      if (!chips.length) {
        this.chipsHost.hidden = true;
        return;
      }

      chips.forEach((chip, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'etolie-collection__active-chip';
        button.dataset.etolieChip = String(index);
        button.setAttribute('aria-label', `${window.etolieT('Usuń filtr')} ${chip.key} ${chip.label}`);
        const key = document.createElement('span');
        key.className = 'etolie-collection__active-chip-key';
        key.textContent = chip.key;
        button.append(key, document.createTextNode(` ${chip.label} `));
        button.insertAdjacentHTML(
          'beforeend',
          '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m4 4 8 8M12 4 4 12"/></svg>',
        );
        this.chipsHost.append(button);
      });
      this.activeChips = chips;
      this.chipsHost.hidden = false;
    }

    async onChipClick(event) {
      const button = event.target instanceof Element ? event.target.closest('[data-etolie-chip]') : null;
      if (!button) return;
      event.preventDefault();
      const chip = this.activeChips?.[Number.parseInt(button.dataset.etolieChip, 10)];
      if (!chip) return;

      if (chip.inputs) chip.inputs.forEach((input) => { input.value = ''; });
      else if (chip.input) {
        if (chip.input.name === CATEGORY_FIELD) {
          const all = this.categoryInput('');
          if (all) all.checked = true;
        } else {
          chip.input.checked = false;
        }
      }
      const locationSequence = ++this.locationSequence;
      await this.deferredFacetPromise;
      if (!this._connected || locationSequence !== this.locationSequence) return;
      await this.loadFirstPage(this.stateFromForm(), { announce: true, purpose: 'chip', historyMode: 'push' });
    }

    categoryInput(handle = '') {
      return Array.from(this.form.elements).find((element) => (
        element instanceof HTMLInputElement
        && element.name === CATEGORY_FIELD
        && element.value === handle
      )) || null;
    }

    selectedCategoryInput() {
      return this.form.querySelector(`input[name="${CATEGORY_FIELD}"]:checked`)
        || this.categoryInput('');
    }

    stateFromForm() {
      const categoryInput = this.selectedCategoryInput();
      const category = categoryInput?.value || '';
      const source = categoryInput?.dataset.etolieCategorySource || this.allSource;
      const parameters = new URLSearchParams();

      new FormData(this.form).forEach((value, name) => {
        if (name === CATEGORY_FIELD || name === 'page' || name === 'view' || name === 'section_id') return;
        const stringValue = String(value).trim();
        if (PRICE_FIELDS.includes(name) && stringValue === '') return;
        parameters.append(name, stringValue);
      });
      // Przed dołączeniem facetów formularz nie ma jeszcze checkboxów koloru
      // i rozmiaru. Wybór kategorii/sortowania w tym czasie zachowuje filtry URL.
      if (this.deferredFilters && !this.facetsReady && this.appliedState) {
        this.appliedState.parameters.forEach((value, name) => {
          if (name.startsWith(FILTER_PREFIX)) parameters.append(name, value);
        });
      }
      if (!parameters.has('sort_by')) parameters.set('sort_by', this.defaultSort);

      return { category, source, parameters: stableParameters(parameters) };
    }

    // Zatwierdzony wybór kategorii zapisujemy ŚCIEŻKĄ kolekcji (ETAP 5), więc
    // z powrotem czytamy go po ścieżce. Parametr `category` zostaje dla starych
    // linków, które jeszcze go niosą.
    categoryFromPathname() {
      const path = window.location.pathname;
      const match = Array.from(this.form.elements).find((element) => {
        if (!(element instanceof HTMLInputElement) || element.name !== CATEGORY_FIELD) return false;
        if (!element.value) return false;
        const source = element.dataset.etolieCategorySource;
        if (!source) return false;
        return new URL(source, window.location.origin).pathname === path;
      });
      return match ? match.value : '';
    }

    stateFromLocation() {
      const locationParameters = new URLSearchParams(window.location.search);
      const requestedCategory = locationParameters.get(HOME_CATEGORY_PARAM) || this.categoryFromPathname();
      const categoryInput = this.categoryInput(requestedCategory) || this.categoryInput('');
      const parameters = new URLSearchParams();

      locationParameters.forEach((value, name) => {
        if (name.startsWith(FILTER_PREFIX) || name === 'sort_by') parameters.append(name, value);
      });
      if (!parameters.has('sort_by')) parameters.set('sort_by', this.defaultSort);

      return {
        category: categoryInput?.value || '',
        source: categoryInput?.dataset.etolieCategorySource || this.allSource,
        parameters: stableParameters(parameters),
      };
    }

    syncForm(state, asApplied = false) {
      const valuesByName = new Map();
      state.parameters.forEach((value, name) => {
        const values = valuesByName.get(name) || new Set();
        values.add(value);
        valuesByName.set(name, values);
      });

      Array.from(this.form.elements).forEach((element) => {
        if (!(element instanceof HTMLInputElement || element instanceof HTMLSelectElement)) return;

        if (element.name === CATEGORY_FIELD && element instanceof HTMLInputElement) {
          element.checked = element.value === state.category;
          if (element.checked) element.disabled = false;
          if (asApplied) element.defaultChecked = element.checked;
          return;
        }

        if (element instanceof HTMLInputElement && (element.type === 'checkbox' || element.type === 'radio')) {
          element.checked = Boolean(valuesByName.get(element.name)?.has(element.value));
          if (element.checked) element.disabled = false;
          if (asApplied) element.defaultChecked = element.checked;
          return;
        }

        if (element.name && (element.name.startsWith(FILTER_PREFIX) || element.name === 'sort_by')) {
          element.value = valuesByName.get(element.name)?.values().next().value || '';
          if (asApplied) element.defaultValue = element.value;
        }
      });

      if (asApplied) this.markFormDefaults();
      this.updateFilterCount(this.stateFromForm());
    }

    markFormDefaults() {
      Array.from(this.form.elements).forEach((element) => {
        if (!(element instanceof HTMLInputElement || element instanceof HTMLSelectElement)) return;
        if (element instanceof HTMLInputElement && (element.type === 'checkbox' || element.type === 'radio')) {
          element.defaultChecked = element.checked;
          element.toggleAttribute('checked', element.checked);
        } else {
          element.defaultValue = element.value;
          element.setAttribute('value', element.value);
        }
      });
    }

    sourceURL(state, pageURL = '') {
      const pageSource = pageURL ? new URL(pageURL, window.location.origin) : null;
      const page = pageSource?.searchParams.get('page') || '';
      const url = new URL(state.source || this.allSource, window.location.origin);
      url.search = '';
      url.hash = '';

      stableParameters(state.parameters).forEach((value, name) => {
        if (name !== CATEGORY_FIELD && name !== 'page' && name !== 'section_id' && name !== 'view') {
          url.searchParams.append(name, value);
        }
      });
      if (!url.searchParams.has('sort_by')) url.searchParams.set('sort_by', this.defaultSort);
      url.searchParams.set('view', this.viewName);
      if (page) url.searchParams.set('page', page);
      return url;
    }

    endpointURL(rawURL, currentURL = '') {
      if (!rawURL) return '';
      const raw = new URL(rawURL, window.location.origin);
      const url = new URL(currentURL || raw.href, window.location.origin);
      if (currentURL) {
        const page = raw.searchParams.get('page');
        if (page) url.searchParams.set('page', page);
        else url.searchParams.delete('page');
      }
      url.searchParams.delete('section_id');
      url.searchParams.set('view', this.viewName);
      return url.href;
    }

    deferredFacetURL() {
      const url = new URL(this.filterSourceURL || this.allSource, window.location.origin);
      url.search = '';
      url.hash = '';
      url.searchParams.set('view', 'etolie-home-filters');
      return url;
    }

    hydrateFacetGroups(filterSource, state = this.appliedState) {
      const sourceGroups = filterSource?.matches?.('[data-etolie-facet-groups]')
        ? filterSource
        : filterSource?.querySelector?.('[data-etolie-facet-groups]');
      if (!this.facetContainer || !sourceGroups) return false;

      const fragment = document.createDocumentFragment();
      Array.from(sourceGroups.children).forEach((group) => fragment.append(group.cloneNode(true)));
      this.facetContainer.replaceChildren(fragment);
      this.facetsReady = true;
      this.syncForm(state || this.stateFromLocation(), true);
      this.facetBaseline = this.captureFacetBaseline();
      this.form.dispatchEvent(new CustomEvent('etolie:facets-updated', {
        bubbles: true,
        detail: { scope: this.facetContainer },
      }));
      return true;
    }

    async bootstrapDeferredFacets() {
      if (!this.deferredFilters || !this.facetContainer) return true;
      this.deferredFacetController?.abort();
      this.deferredFacetController = new AbortController();

      try {
        const response = await fetch(this.deferredFacetURL().href, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: this.deferredFacetController.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const responseDocument = new DOMParser().parseFromString(html, 'text/html');
        const filterSource = responseDocument.querySelector('[data-home-filter-source]');
        if (!this.hydrateFacetGroups(filterSource, this.appliedState)) {
          throw new Error('Brak facetów home w odpowiedzi');
        }
        return true;
      } catch (error) {
        if (error.name === 'AbortError') return false;
        const status = this.facetContainer.querySelector('[data-etolie-deferred-filter-status]');
        if (status) status.textContent = window.etolieT("Filtry są chwilowo niedostępne.");
        return false;
      } finally {
        this.deferredFacetController = null;
      }
    }

    // Klucz stanu katalogu — po nim popstate poznaje, czy Wstecz/Dalej naprawdę
    // zmieniło wybór, czy tylko kotwicę albo warstwę koszyka na tym samym adresie.
    catalogStateKey(state) {
      return JSON.stringify([state.category, stableParameters(state.parameters).toString()]);
    }

    syncCategoryRow(state) {
      this.querySelectorAll('[data-etolie-category-handle]').forEach((link) => {
        const current = link.dataset.etolieCategoryHandle === state.category;
        link.classList.toggle('is-current', current);
        if (current) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
    }

    setPending(pending) {
      this.classList.toggle(PENDING_CLASS, pending);
    }

    async requestSnapshot(url, signal) {
      const key = url.href || String(url);
      const cached = this.responseCache.get(key);
      if (cached) {
        this.responseCache.delete(key);
        this.responseCache.set(key, cached);
        return cached;
      }

      const response = await fetch(key, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const responseDocument = new DOMParser().parseFromString(html, 'text/html');
      const responseRoot = responseDocument.querySelector('[data-home-catalog-response]');
      const scope = responseRoot || responseDocument;
      const gridSource = scope.querySelector('[data-home-grid-source]')
        || scope.querySelector('[data-etolie-collection-grid]');
      const filterSource = scope.querySelector('[data-home-filter-source]')
        || scope.querySelector('form.etolie-collection__controls');
      const countNode = responseRoot
        || scope.querySelector('[data-results-count]')
        || scope.querySelector('[data-etolie-results-count]');
      if (!gridSource) throw new Error('Brak siatki produktów w odpowiedzi');

      const rawCount = countNode?.dataset.resultsCount ?? countNode?.dataset.etolieResultsCount;
      const parsedCount = Number.parseInt(rawCount, 10);
      const nextLink = scope.querySelector('[data-etolie-next-page]');
      const rawNextURL = responseRoot?.dataset.nextUrl || nextLink?.getAttribute('href') || '';
      const snapshot = {
        url: key,
        gridSource,
        filterSource,
        count: Number.isFinite(parsedCount) ? parsedCount : null,
        requiresExactVariantFilter: responseRoot?.dataset.requiresExactVariantFilter === 'true',
        nextURL: this.endpointURL(rawNextURL, key),
      };

      this.responseCache.set(key, snapshot);
      while (this.responseCache.size > CACHE_LIMIT) {
        this.responseCache.delete(this.responseCache.keys().next().value);
      }
      return snapshot;
    }

    snapshotCardCount(snapshot) {
      return Array.from(snapshot.gridSource.children).filter((child) => child.matches('.etolie-card')).length;
    }

    async firstSnapshotWithCards(url, signal, maximumPages = 40) {
      let nextURL = url;
      let lastSnapshot = null;
      let firstFilterSource = null;
      let requiresExactVariantFilter = false;
      const visited = new Set();

      while (nextURL && visited.size < maximumPages) {
        const key = nextURL.href || String(nextURL);
        if (visited.has(key)) break;
        visited.add(key);
        lastSnapshot = await this.requestSnapshot(nextURL, signal);
        if (!firstFilterSource && lastSnapshot.filterSource) firstFilterSource = lastSnapshot.filterSource;
        requiresExactVariantFilter ||= lastSnapshot.requiresExactVariantFilter;
        if (this.snapshotCardCount(lastSnapshot) > 0 || !lastSnapshot.nextURL) break;
        nextURL = new URL(lastSnapshot.nextURL, window.location.origin);
      }

      if (!lastSnapshot) throw new Error('Brak odpowiedzi katalogu');
      if (!lastSnapshot.filterSource && firstFilterSource) {
        return { ...lastSnapshot, filterSource: firstFilterSource, requiresExactVariantFilter };
      }
      return { ...lastSnapshot, requiresExactVariantFilter };
    }

    cloneCards(gridSource, { lazy = false, deduplicate = false } = {}) {
      const fragment = document.createDocumentFragment();
      const existingIds = deduplicate
        ? new Set(Array.from(this.grid.querySelectorAll(':scope > .etolie-card[data-product-id]')).map((card) => card.dataset.productId))
        : new Set();
      let count = 0;

      Array.from(gridSource.children).filter((child) => child.matches('.etolie-card')).forEach((card) => {
        const productId = card.dataset.productId || '';
        if (deduplicate && productId && existingIds.has(productId)) return;
        if (productId) existingIds.add(productId);

        const clone = card.cloneNode(true);
        clone.querySelectorAll('script').forEach((script) => script.remove());
        if (lazy) {
          clone.querySelectorAll('img').forEach((image) => {
            image.loading = 'lazy';
            image.fetchPriority = 'auto';
            image.setAttribute('loading', 'lazy');
            image.setAttribute('fetchpriority', 'auto');
          });
        }
        fragment.append(clone);
        count += 1;
      });
      return { fragment, count };
    }

    async loadFirstPage(state, { announce = true, scrollToStart = false, purpose = 'apply', historyMode = 'push' } = {}) {
      this.homeGridIsSSR = false;
      this.primarySequence += 1;
      const sequence = this.primarySequence;
      this.primaryController?.abort();
      this.primaryController = new AbortController();
      this.primaryPurpose = purpose;
      this.progressiveSequence += 1;
      this.progressiveController?.abort();
      this.progressiveController = null;
      this.loadingMore = false;
      this.intersectionObserver?.disconnect();
      if (purpose === 'initial' || purpose === 'history') this.setPending(true);
      this.setBusy(true);

      try {
        const snapshot = await this.firstSnapshotWithCards(
          this.sourceURL(state),
          this.primaryController.signal,
        );
        if (sequence !== this.primarySequence) return false;
        this.commitSnapshot(snapshot, state, { announce, scrollToStart, historyMode });
        this.retryState = null;
        this.retryScroll = false;
        return true;
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.primarySequence) return false;
        this.retryState = cloneState(state);
        this.retryScroll = scrollToStart;
        this.retryHistoryMode = historyMode;
        this.showRetry(window.etolieT("Spróbuj ponownie"));
        this.announce(window.etolieT("Nie udało się odświeżyć produktów. Spróbuj ponownie."));
        return false;
      } finally {
        if (sequence === this.primarySequence) {
          this.primaryController = null;
          this.primaryPurpose = '';
          this.setBusy(false);
        }
      }
    }

    commitSnapshot(snapshot, state, { announce = true, scrollToStart = false, historyMode = 'push' } = {}) {
      const { fragment, count: renderedCount } = this.cloneCards(snapshot.gridSource);
      this.progressiveSequence += 1;
      this.progressiveController?.abort();
      this.intersectionObserver?.disconnect();
      window.etolieDestroyCards?.(this.grid);
      this.grid.replaceChildren(fragment);
      if (!renderedCount) this.renderEmptyResult();
      window.etolieInitCards?.(this.grid);

      this.appliedState = cloneState(state);
      this.appliedSnapshot = snapshot;
      this.nextURL = snapshot.nextURL;
      this.requiresExactVariantFilter = Boolean(snapshot.requiresExactVariantFilter);
      this.loadedPageURLs = new Set([snapshot.url]);
      if (!this.hydrateFacetGroups(snapshot.filterSource, this.appliedState)) {
        this.syncForm(this.appliedState, true);
        this.facetBaseline = this.captureFacetBaseline();
      }
      const countIsMinimum = this.requiresExactVariantFilter && Boolean(snapshot.nextURL);
      // 28.09.2026 (N5): snapshot.count (products_count) liczy też wyprzedane; bez niego
      // liczba jest znana tylko przy jednej stronie albo jako „co najmniej”.
      const resolvedCount = this.requiresExactVariantFilter || !snapshot.nextURL
        ? renderedCount
        : null;
      this.updateResultCount(resolvedCount, { minimum: countIsMinimum });
      this.syncCategoryRow(this.appliedState);
      // Szkielet zdejmujemy dopiero po poprawnej odpowiedzi, nie po samym żądaniu.
      this.setPending(false);
      this.interleaveBrandStories();
      this.prepareProgressiveLoading();
      this.closeSharedPanel();
      // 04.09.2026: stan filtra widać na pasku chipów i w adresie, a adres jest
      // taki, który serwer umie wyrenderować od pierwszego malowania.
      this.visiblePage = 1;
      this.tagStreamPage(this.grid.children, 1);
      this.renderActiveChips();
      this.syncHistoryURL(1, historyMode);

      if (scrollToStart) window.requestAnimationFrame(() => this.scrollToCatalogStart());
      this.dispatchEvent(new CustomEvent('etolie:listing-committed', {
        bubbles: true,
        detail: { category: state.category, parameters: state.parameters.toString() },
      }));
      if (announce) this.announce(window.etolieT('Wyświetlam {count}.', {count: formatCatalogCount(resolvedCount, countIsMinimum)}));
    }

    scrollToCatalogStart() {
      const target = this.querySelector('.etolie-home-catalog__header') || this;
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({
        block: 'start',
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    }

    renderEmptyResult() {
      const message = document.createElement('p');
      message.className = 'etolie-home-catalog__empty';
      message.textContent = window.etolieT("Nie znaleźliśmy produktów dla tych filtrów.");
      message.setAttribute('role', 'status');
      this.grid.append(message);
    }

    closeSharedPanel() {
      if (!this.filterDetails?.open) return;
      this.filterDetails.open = false;
      window.requestAnimationFrame(() => {
        this.filterDetails?.querySelector('summary')?.focus({ preventScroll: true });
      });
    }

    setBusy(busy) {
      this.toggleAttribute('aria-busy', busy);
      this.grid?.toggleAttribute('aria-busy', busy);
      this.filterDetails?.toggleAttribute('inert', busy);
      if (this.submitButton) {
        this.submitButton.disabled = busy;
        this.submitButton.toggleAttribute('aria-busy', busy);
      }
    }

    updateResultCount(count, { minimum = false } = {}) {
      if (this.resultCount) this.resultCount.textContent = formatCatalogCount(count, minimum);
      if (this.submitLabel) {
        this.submitLabel.textContent = Number.isFinite(count) ? formatSubmitCount(count, minimum) : window.etolieT('Pokaż wyniki');
      }
      this.appliedCount = count;
      this.appliedCountIsMinimum = minimum;
    }

    announce(message) {
      if (!this.status) return;
      this.status.textContent = '';
      window.requestAnimationFrame(() => { this.status.textContent = message; });
    }

    captureFacetBaseline() {
      const baseline = new Map();
      this.form.querySelectorAll(`input[name^="${FILTER_PREFIX}"]`).forEach((input) => {
        const label = input.closest('label');
        const countNode = label?.querySelector('.etolie-collection__filter-option-count');
        baseline.set(input, {
          disabled: input.disabled,
          count: countNode?.textContent ?? null,
          min: input.getAttribute('min'),
          max: input.getAttribute('max'),
          step: input.getAttribute('step'),
          placeholder: input.getAttribute('placeholder'),
        });
      });
      return baseline;
    }

    restoreFacetBaseline() {
      this.facetBaseline?.forEach((state, input) => {
        if (!input.isConnected) return;
        input.disabled = state.disabled;
        const label = input.closest('label');
        label?.classList.toggle('etolie-collection__filter-option--disabled', input.disabled);
        const countNode = label?.querySelector('.etolie-collection__filter-option-count');
        if (countNode && state.count !== null) countNode.textContent = state.count;

        ['min', 'max', 'step', 'placeholder'].forEach((attribute) => {
          const value = state[attribute];
          if (value === null) input.removeAttribute(attribute);
          else input.setAttribute(attribute, value);
        });
      });
    }

    activeFilterCount(state) {
      // 04.09.2026: jedna rodzina koloru („Zielony") jedzie w adresie jako kilka
      // surowych wartości (Zielony, Khaki, Oliwkowy, Miętowy). Licznik przy pigułce
      // ma pokazywać rodziny, więc doklejone wartości z data-etolie-more pomijamy.
      const extraValues = new Set();
      this.form?.querySelectorAll('input[data-etolie-more]').forEach((input) => {
        input.dataset.etolieMore.split('~|~').forEach((value) => {
          if (value) extraValues.add(`${input.name}=${value}`);
        });
      });

      let count = state.category ? 1 : 0;
      let hasPrice = false;
      state.parameters.forEach((value, name) => {
        if (PRICE_FIELDS.includes(name)) hasPrice = true;
        else if (name.startsWith(FILTER_PREFIX) && !extraValues.has(`${name}=${value}`)) count += 1;
      });
      if (hasPrice) count += 1;
      return count;
    }

    updateFilterCount(state) {
      const summary = this.filterDetails?.querySelector(':scope > summary');
      if (!summary) return;
      const count = this.activeFilterCount(state);
      const sort = state.parameters.get('sort_by') || this.defaultSort;
      // 04.09.2026: „Wyczyść" zostaje w stopce zawsze — znikające zmieniało jej
      // układ. Nieaktywny stan opisuje aria-disabled, tak jak na kolekcji.
      if (this.resetButton) {
        this.resetButton.hidden = false;
        this.resetButton.toggleAttribute('aria-disabled', count === 0 && sort === this.defaultSort);
      }
      let badge = summary.querySelector('.etolie-collection__filter-count');
      if (!count) {
        badge?.remove();
        return;
      }
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'etolie-collection__filter-count';
        summary.append(badge);
      }
      badge.textContent = String(count);
      badge.setAttribute('aria-label', `${window.etolieT('Aktywne filtry')}: ${count}`);
    }

    isDefaultStream(state = this.appliedState) {
      if (!state || state.category) return false;
      let isDefault = true;
      state.parameters.forEach((value, name) => {
        if (name.startsWith(FILTER_PREFIX)) isDefault = false;
        if (name === 'sort_by' && value !== this.defaultSort) isDefault = false;
      });
      return isDefault;
    }

    interleaveBrandStories() {
      // Home films use actual visible card positions; AJAX cards remain unchanged.
      const filmsTemplate = this.querySelector('template[data-home-films-template]');
      if (filmsTemplate && this.isDefaultStream() && !this.grid.querySelector('etolie-films')) {
        const anchor = this.grid.querySelectorAll(':scope > .etolie-card')[3];
        const films = filmsTemplate.content.firstElementChild;
        if (anchor && films) anchor.after(films.cloneNode(true));
      }
      if (!this.isDefaultStream() || !this.brandTemplates.length) return;

      const cards = Array.from(this.grid.children).filter((child) => child.matches('.etolie-card'));
      const existing = new Set(
        Array.from(this.grid.children)
          .filter((child) => child.matches('[data-home-brand-story][data-brand-index]'))
          .map((story) => story.dataset.brandIndex),
      );
      // Jeden moduł marki co 12 kart, tyle modułów ile marek w menu.
      this.brandTemplates.map((_, index) => 12 * (index + 1)).forEach((position, index) => {
        const template = this.brandTemplates[index];
        const anchor = cards[position - 1];
        if (!template || !anchor || existing.has(String(index))) return;
        const story = template.content.firstElementChild?.cloneNode(true);
        if (story) {
          story.dataset.brandIndex = String(index);
          anchor.after(story);
        }
      });
    }

    prepareProgressiveLoading() {
      this.intersectionObserver?.disconnect();
      // 20.10.2026: home bez sentinela = numerowane strony SSR. Po podmianie siatki w miejscu
      // (filtr, sortowanie, kategoria, reset) nawigacja znika, a kolejne porcje daje przycisk.
      if (this.homePagination) this.homePagination.hidden = !this.homeGridIsSSR;
      if (!this.nextURL || !this.sentinel) {
        if (this.loadMoreButton) {
          if (this.mode === 'catalog' && this.nextURL && !this.homeGridIsSSR) this.showRetry(window.etolieT("Pokaż więcej"));
          else this.loadMoreButton.hidden = true;
        }
        return;
      }

      if (!('IntersectionObserver' in window)) {
        // Bez obserwatora kolekcja wraca do pełnej paginacji, a home do przycisku.
        if (this.mode === 'stream') this.revealPagination();
        else this.showRetry(window.etolieT("Pokaż więcej"));
        return;
      }

      if (this.loadMoreButton) this.loadMoreButton.hidden = true;
      // Kolekcja doczytuje z wyprzedzeniem 1400 px (tak było w loaderze sekcji),
      // home 700 px — obie wartości zostają takie jak przed scaleniem.
      const rootMargin = this.mode === 'stream' ? '1400px 0px' : '700px 0px';
      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        if (this.mode === 'stream') this.loadNextStreamPage();
        else this.loadNextPage();
      }, { rootMargin });
      this.intersectionObserver.observe(this.sentinel);
    }

    showRetry(label) {
      if (!this.loadMoreButton) return;
      this.loadMoreButton.textContent = label;
      this.loadMoreButton.hidden = false;
    }

    async loadNextPage() {
      if (!this.nextURL || this.loadingMore) return;
      const requestedURL = this.endpointURL(this.nextURL);
      if (!requestedURL || this.loadedPageURLs.has(requestedURL)) return;

      this.loadingMore = true;
      this.progressiveSequence += 1;
      const sequence = this.progressiveSequence;
      this.progressiveController?.abort();
      this.progressiveController = new AbortController();
      this.intersectionObserver?.unobserve(this.sentinel);
      if (this.loadMoreButton) this.loadMoreButton.hidden = true;
      this.sentinel?.setAttribute('aria-busy', 'true');

      try {
        let pageURL = requestedURL;
        let appendedCount = 0;
        let scannedPages = 0;

        while (pageURL && scannedPages < 40 && appendedCount === 0) {
          if (this.loadedPageURLs.has(pageURL)) {
            this.nextURL = '';
            break;
          }
          const snapshot = await this.requestSnapshot(new URL(pageURL), this.progressiveController.signal);
          if (sequence !== this.progressiveSequence) return;
          this.loadedPageURLs.add(pageURL);
          scannedPages += 1;

          const { fragment, count } = this.cloneCards(snapshot.gridSource, { lazy: true, deduplicate: true });
          appendedCount = count;
          this.nextURL = snapshot.nextURL;
          if (count > 0) {
            const appendedCards = Array.from(fragment.children);
            this.grid.append(fragment);
            appendedCards.forEach((card) => window.etolieInitCards?.(card));
            const loadedPage = Number.parseInt(new URL(pageURL, window.location.origin).searchParams.get('page') || '1', 10) || 1;
            this.tagStreamPage(appendedCards, loadedPage);
            this.interleaveBrandStories();
          }
          pageURL = snapshot.nextURL;
        }

        const visibleTotal = this.grid.querySelectorAll(':scope > .etolie-card').length;
        const countIsMinimum = this.requiresExactVariantFilter && Boolean(this.nextURL);
        if (this.requiresExactVariantFilter || !this.nextURL) {
          this.updateResultCount(visibleTotal, { minimum: countIsMinimum });
        }
        if (appendedCount > 0) this.announce(window.etolieT('Załadowano {count}.', {count: formatProducts(appendedCount)}));
        if (this.nextURL) {
          if (this.sentinel) this.intersectionObserver?.observe(this.sentinel);
          else this.showRetry(window.etolieT("Pokaż więcej"));
        } else {
          if (this.loadMoreButton) this.loadMoreButton.hidden = true;
        }
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.progressiveSequence) return;
        this.showRetry(window.etolieT("Spróbuj ponownie"));
        this.announce(window.etolieT("Nie udało się wczytać kolejnych produktów. Spróbuj ponownie."));
      } finally {
        if (sequence === this.progressiveSequence) {
          this.loadingMore = false;
          this.progressiveController = null;
          this.sentinel?.removeAttribute('aria-busy');
        }
      }
    }

    onFormChange() {
      if (this.primaryPurpose === 'initial') {
        this.primarySequence += 1;
        this.primaryController?.abort();
        this.primaryController = null;
        this.primaryPurpose = '';
        this.setBusy(false);
      }
      const state = this.stateFromForm();
      this.updateFilterCount(state);
      if (this.submitLabel) this.submitLabel.textContent = window.etolieT("Pokaż wyniki");
    }

    onFormInput(event) {
      if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'number') return;
      const state = this.stateFromForm();
      this.updateFilterCount(state);
      if (this.submitLabel) this.submitLabel.textContent = window.etolieT("Pokaż wyniki");
    }

    async onSubmit(event) {
      event.preventDefault();
      const locationSequence = ++this.locationSequence;
      const state = this.stateFromForm();
      await this.deferredFacetPromise;
      if (!this._connected || locationSequence !== this.locationSequence) return;
      await this.loadFirstPage(state, { announce: true, scrollToStart: true, purpose: 'apply', historyMode: 'push' });
    }

    async onResetClick(event) {
      event.preventDefault();
      const locationSequence = ++this.locationSequence;
      await this.deferredFacetPromise;
      if (!this._connected || locationSequence !== this.locationSequence) return;
      const allCategory = this.categoryInput('');
      const state = {
        category: '',
        source: allCategory?.dataset.etolieCategorySource || this.allSource,
        parameters: new URLSearchParams([['sort_by', this.defaultSort]]),
      };
      this.syncForm(state, false);
      await this.loadFirstPage(state, { announce: true, scrollToStart: true, purpose: 'reset', historyMode: 'push' });
    }

    onFilterToggle() {
      if (this.filterDetails?.open) return;
      window.requestAnimationFrame(() => {
        const state = this.stateFromForm();
        this.updateFilterCount(state);
        if (this.submitLabel && Number.isFinite(this.appliedCount)) {
          this.submitLabel.textContent = formatSubmitCount(this.appliedCount, this.appliedCountIsMinimum);
        }
        this.restoreFacetBaseline();
      });
    }

    onLoadMoreClick() {
      if (this.retryState) {
        const state = cloneState(this.retryState);
        const scrollToStart = Boolean(this.retryScroll);
        const historyMode = this.retryHistoryMode || 'push';
        this.retryState = null;
        this.retryScroll = false;
        this.loadFirstPage(state, { announce: true, scrollToStart, purpose: 'retry', historyMode });
      } else {
        this.loadNextPage();
      }
    }

    async onPopState() {
      const state = this.stateFromLocation();
      const stateKey = this.catalogStateKey(state);
      // Koszyk mobile używa własnego pushState/back na tym samym adresie.
      // Jego zamknięcie (ani zmiana kotwicy) nie resetuje doładowanej siatki.
      if (stateKey === this.locationStateKey) return;
      this.locationStateKey = stateKey;
      const locationSequence = ++this.locationSequence;
      // Wstecz ma pierwszeństwo również przed odpowiedzią trwającego „Pokaż”.
      this.primarySequence += 1;
      this.primaryController?.abort();
      this.progressiveSequence += 1;
      this.progressiveController?.abort();
      this.intersectionObserver?.disconnect();
      this.setPending(true);
      await this.deferredFacetPromise;
      if (!this._connected || locationSequence !== this.locationSequence) return;
      await this.loadFirstPage(state, { announce: true, purpose: 'history', historyMode: 'none' });
    }

    // BFCache zachowuje gotową siatkę; popstate obsługuje zmianę wyboru w historii.
    onPageShow(event) {
      if (!event.persisted) return;
      window.requestAnimationFrame(() => {
        this.syncForm(this.appliedState, true);
        this.syncCategoryRow(this.appliedState);
        this.interleaveBrandStories();
        this.prepareProgressiveLoading();
      });
    }
  }

  customElements.define('etolie-listing-stream', EtolieListingStream);
})();
